import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MessageCircle, Phone, ShieldCheck, Sparkles, Clock3, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { serviceBlurbs, studio, whatsappLink } from '../mock';
import Logo from './Logo';

const ServiceEnquiryContext = createContext(null);

export function useServiceEnquiry() {
  const ctx = useContext(ServiceEnquiryContext);
  if (!ctx) throw new Error('useServiceEnquiry must be used within ServiceEnquiryProvider');
  return ctx;
}

const perks = [
  { icon: ShieldCheck, label: 'Precision fit' },
  { icon: Sparkles, label: 'Premium films' },
  { icon: Clock3, label: 'Fast reply' },
];

export function ServiceEnquiryProvider({ children }) {
  const [selected, setSelected] = useState(null);
  const [vehicle, setVehicle] = useState('');

  const openService = useCallback((name, meta = {}) => {
    setVehicle('');
    setSelected({ name, category: meta.category || 'VINYLED', image: meta.image });
  }, []);

  const close = useCallback(() => setSelected(null), []);

  useEffect(() => {
    if (!selected) setVehicle('');
  }, [selected]);

  const value = useMemo(() => ({ openService }), [openService]);
  const href = selected ? whatsappLink(selected.name, { vehicle: vehicle.trim() }) : whatsappLink();
  const blurb = selected
    ? (serviceBlurbs[selected.name] || 'Professional installation by VINYLED in Bengaluru. Continue on WhatsApp for a quote.')
    : '';

  return (
    <ServiceEnquiryContext.Provider value={value}>
      {children}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent
          hideClose
          className="left-1/2 top-1/2 w-[min(calc(100%-1.5rem),400px)] max-h-[min(86dvh,620px)] -translate-x-1/2 -translate-y-1/2 gap-0 overflow-y-auto rounded-2xl border-0 bg-white p-0 shadow-[0_24px_80px_-18px_rgba(20,20,15,0.55)] sm:rounded-[1.6rem]"
        >
          <div className="relative h-[108px] sm:h-[140px] overflow-hidden bg-[#16150f] shrink-0">
            {selected?.image ? (
              <img src={selected.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(255,90,31,0.55),transparent_52%)]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#16150f] via-[#16150f]/50 to-black/10" />

            <Logo variant="white" className="absolute left-4 top-3.5 h-5 sm:left-5 sm:top-4 sm:h-6" />
            <DialogClose className="absolute right-3 top-3 sm:right-4 sm:top-3.5 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#16150f] shadow-sm">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>

            <span className="absolute bottom-3 left-4 sm:bottom-4 sm:left-5 inline-flex rounded-full bg-white/15 px-2.5 py-1 text-[0.6rem] font-semibold tracking-[0.14em] text-white backdrop-blur-md">
              {selected?.category}
            </span>
          </div>

          <div className="p-4 sm:p-5">
            <DialogHeader className="space-y-1.5 text-left">
              <p className="text-[0.6rem] font-semibold tracking-[0.18em] text-[#ff5a1f]">WHATSAPP ENQUIRY</p>
              <DialogTitle className="font-display text-[1.35rem] sm:text-[1.55rem] font-extrabold leading-tight tracking-tight text-[#16150f]">
                {selected?.name}
              </DialogTitle>
              <DialogDescription className="text-[0.88rem] sm:text-[0.92rem] leading-relaxed text-[#5a5548]">
                {blurb}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-3.5 grid grid-cols-3 gap-1.5 sm:gap-2">
              {perks.map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-1 rounded-xl bg-[#f7f6f2] px-1.5 py-2.5 text-center">
                  <Icon className="h-3.5 w-3.5 text-[#ff5a1f]" />
                  <span className="text-[0.64rem] font-medium text-[#4a463d] leading-tight">{label}</span>
                </div>
              ))}
            </div>

            <label className="mt-4 block">
              <span className="mb-1.5 block text-[0.62rem] font-semibold tracking-widest text-[#8a8578]">VEHICLE (OPTIONAL)</span>
              <input
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
                placeholder="e.g. BMW X5 / Hunter 350"
                className="w-full rounded-xl border border-black/8 bg-[#faf8f3] px-3.5 py-2.5 text-sm text-[#16150f] placeholder:text-[#b5b0a3] outline-none transition focus:border-[#ff5a1f] focus:ring-2 focus:ring-[#ff5a1f]/15"
              />
            </label>

            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="mt-3.5 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_-10px_rgba(37,211,102,0.95)] active:scale-[0.98] transition-transform"
            >
              <MessageCircle className="h-4 w-4 fill-white" />
              Get quote on WhatsApp
            </a>

            <a
              href={studio.phoneHref}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#ff5a1f] px-5 py-3 text-sm font-semibold text-white active:scale-[0.98] transition-transform"
            >
              <Phone className="h-4 w-4" /> Call now
            </a>

            <p className="mt-2.5 text-center text-[0.65rem] leading-relaxed text-[#8a8578]">
              WhatsApp opens with this service pre-filled
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </ServiceEnquiryContext.Provider>
  );
}
